import { useEffect, useRef, useState } from "react"
import { createCodeGroup, renameCodeGroup, deleteCodeGroup, fetchNotificationSettings } from "../../lib/api"
import AlertRuleEditor from "../alerts/AlertRuleEditor"

// Create, rename and delete code groups. Folders are free on every plan, so
// there's no lock state here. Deleting a group only ungroups its codes — the
// copy says so, because "delete" next to printed codes reads as scary.
//
// Built on the native <dialog> like EditCodeModal: Esc, the scrim and focus
// restore come for free.
export default function GroupsDialog({ groups, onClose, onChanged }) {
  const dialogRef = useRef(null)
  const [name, setName] = useState("")
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)
  // Scan targets per group (#304), only while that email is on.
  const [alertsOn, setAlertsOn] = useState(false)

  useEffect(() => {
    fetchNotificationSettings()
      .then((s) => setAlertsOn(!!(s.features?.scan_alert_rules || s.features?.quiet_alerts)))
      .catch(() => {})
  }, [])

  useEffect(() => {
    const dialog = dialogRef.current
    if (dialog && !dialog.open) dialog.showModal()
    function handleCancel(e) { e.preventDefault(); onClose() }
    function handleClick(e) { if (e.target === dialog) onClose() }
    dialog?.addEventListener("cancel", handleCancel)
    dialog?.addEventListener("click", handleClick)
    return () => {
      dialog?.removeEventListener("cancel", handleCancel)
      dialog?.removeEventListener("click", handleClick)
    }
  }, [onClose])

  // Two awaits, two meanings: only the action's failure is the action's
  // failure. Reporting a failed refresh as one told the user their group
  // wasn't created, and the retry then collided with the group that was.
  async function run(action) {
    setBusy(true)
    setError(null)
    try {
      await action()
    } catch (e) {
      setError(cleanMessage(e))
      return false
    } finally {
      setBusy(false)
    }
    try {
      await onChanged()
    } catch {
      // The action landed; stale counts aren't worth reporting as its failure.
    }
    return true
  }

  async function handleCreate(e) {
    e.preventDefault()
    if (!name.trim()) return
    if (await run(() => createCodeGroup(name.trim()))) setName("")
  }

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby="groups-dialog-title"
      className="p-0 m-auto bg-transparent backdrop:bg-coal/55 backdrop:backdrop-blur-sm"
    >
      <div className="w-[min(92vw,440px)] max-h-[85dvh] flex flex-col bg-white dark:bg-midnight text-ink dark:text-cloud rounded-xl shadow-lg overflow-hidden">
        <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-3 border-b border-[color:var(--color-border)] dark:border-storm">
          <div>
            <h2 id="groups-dialog-title" className="font-display font-bold text-lg">Groups</h2>
            <p className="text-xs text-[color:var(--color-muted)] dark:text-muted-light mt-0.5">
              One group per code. Deleting a group keeps its codes — they just become ungrouped.
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close"
            className="text-[color:var(--color-muted)] dark:text-muted-light hover:text-coal dark:hover:text-cloud p-1 bg-transparent border-0 cursor-pointer">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true"><path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" /></svg>
          </button>
        </div>

        <form onSubmit={handleCreate} className="flex gap-2 px-5 py-3 border-b border-[color:var(--color-border)] dark:border-storm">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={80}
            placeholder="New group, e.g. Store 12"
            aria-label="New group name"
            autoFocus
            className="flex-1 min-w-0 border border-[color:var(--color-border)] dark:border-storm rounded px-3 py-2 text-sm bg-white dark:bg-midnight focus:outline-none focus:ring-2 focus:ring-voltage focus:ring-offset-1 dark:focus:ring-offset-midnight focus:border-voltage"
          />
          <button type="submit" disabled={busy || !name.trim()}
            className="bg-voltage text-coal rounded-lg px-4 py-2 text-sm font-semibold hover:bg-voltage-dark transition disabled:opacity-50">
            Add
          </button>
        </form>

        {error && <p role="alert" className="px-5 pt-3 text-xs text-fail">{error}</p>}

        <ul className="overflow-y-auto px-2 py-2">
          {groups.length === 0 && (
            <li className="px-3 py-6 text-center text-sm text-[color:var(--color-muted)] dark:text-muted-light">
              No groups yet. Add one for each store, client or campaign.
            </li>
          )}
          {groups.map((g) => (
            <GroupRow key={g.id} group={g} busy={busy} alertsOn={alertsOn}
              onRename={(newName) => run(() => renameCodeGroup(g.id, newName))}
              onDelete={() => run(() => deleteCodeGroup(g.id))} />
          ))}
        </ul>
      </div>
    </dialog>
  )
}

function GroupRow({ group, busy, alertsOn, onRename, onDelete }) {
  const [editing, setEditing] = useState(false)
  const [alertsOpen, setAlertsOpen] = useState(false)
  const [value, setValue] = useState(group.name)
  const [confirming, setConfirming] = useState(false)

  async function save(e) {
    e.preventDefault()
    const next = value.trim()
    if (!next || next === group.name) { setEditing(false); setValue(group.name); return }
    if (await onRename(next)) setEditing(false)
  }

  if (editing) {
    return (
      <li className="px-3 py-2">
        <form onSubmit={save} className="flex gap-2">
          <input type="text" value={value} onChange={(e) => setValue(e.target.value)} maxLength={80} autoFocus
            aria-label={`Rename ${group.name}`}
            className="flex-1 min-w-0 border border-[color:var(--color-border)] dark:border-storm rounded px-2.5 py-1.5 text-sm bg-white dark:bg-midnight focus:outline-none focus:ring-2 focus:ring-voltage focus:ring-offset-1 dark:focus:ring-offset-midnight" />
          <button type="submit" disabled={busy} className="text-sm font-semibold text-voltage-dark dark:text-voltage-light px-2">Save</button>
          <button type="button" onClick={() => { setEditing(false); setValue(group.name) }} className="text-sm text-[color:var(--color-muted)] dark:text-muted-light px-1">Cancel</button>
        </form>
      </li>
    )
  }

  return (
    <li className="px-3 py-2 rounded hover:bg-[color:var(--color-cloud)] dark:hover:bg-coal">
      <div className="flex items-center gap-2">
        <span className="flex-1 min-w-0 truncate text-sm">{group.name}</span>
        <span className="font-mono text-xs text-[color:var(--color-muted)] dark:text-muted-light">
          {group.code_count} {group.code_count === 1 ? "code" : "codes"}
        </span>
        {confirming ? (
          <span className="flex items-center gap-1.5 text-xs">
            <span className="text-[color:var(--color-muted)] dark:text-muted-light">Ungroup its codes?</span>
            <button type="button" disabled={busy} onClick={onDelete} className="font-semibold text-fail hover:underline">Delete</button>
            <button type="button" onClick={() => setConfirming(false)} className="text-[color:var(--color-muted)] dark:text-muted-light hover:underline">Keep</button>
          </span>
        ) : (
          <>
            {alertsOn && (
              <button type="button" onClick={() => setAlertsOpen((o) => !o)} aria-expanded={alertsOpen}
                className="text-xs text-[color:var(--color-muted)] dark:text-muted-light hover:text-coal dark:hover:text-cloud px-1.5">Alerts</button>
            )}
            <button type="button" onClick={() => setEditing(true)} className="text-xs text-[color:var(--color-muted)] dark:text-muted-light hover:text-coal dark:hover:text-cloud px-1.5">Rename</button>
            <button type="button" onClick={() => setConfirming(true)} className="text-xs text-fail/80 hover:text-fail px-1.5">Delete</button>
          </>
        )}
      </div>
      {alertsOn && alertsOpen && (
        <div className="mt-2 mb-1 pl-0.5">
          <AlertRuleEditor target={{ groupId: group.id }} />
        </div>
      )}
    </li>
  )
}

// Rails' RecordInvalid message is "Validation failed: Name is already used by
// another group"; the prefix is noise to a person.
export function cleanMessage(error) {
  return (error?.message || "Something went wrong. Try again.").replace(/^Validation failed:\s*/, "")
}
